/**
 * apps/automations-portal/src/lib/format.ts
 *
 * Small, pure formatting helpers for the ledger dashboard. Money is always
 * integer pence over the wire — these functions are the only place that
 * turns it (or a raw ISO timestamp) into something a person reads.
 */

/** `45000 -> '£450.00'`. `null`/`undefined` (no figure to show) -> '—'. */
export function formatPence(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return '—';
  const pounds = pence / 100;
  return `£${pounds.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const RELATIVE_CUTOFF_MS = 30 * DAY_MS;

/**
 * Human relative time from now, e.g. 'just now', '5 minutes ago',
 * '2 hours ago', '3 days ago' — falling back to an en-GB date beyond
 * ~30 days, where a relative phrase stops being useful.
 * `null`/`undefined` -> '—'.
 */
export function formatRelativeTime(iso: string | null | undefined, opts?: { suffix?: string }): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const suffix = opts?.suffix ?? ' ago';
  const diffMs = Date.now() - date.getTime();

  if (diffMs < MINUTE_MS) return 'just now';

  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS);
    return `${minutes} minute${minutes === 1 ? '' : 's'}${suffix}`;
  }

  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return `${hours} hour${hours === 1 ? '' : 's'}${suffix}`;
  }

  if (diffMs < RELATIVE_CUTOFF_MS) {
    const days = Math.floor(diffMs / DAY_MS);
    return `${days} day${days === 1 ? '' : 's'}${suffix}`;
  }

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** en-GB date + time, matching the convention already used in HealthPage.tsx. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB');
}
